import { useState } from 'react';
import { Button } from '../components/Button/Button';
import { useFileAppeal, useMyAppeal } from './useAppeal';
import { useCurrentUser } from './useAuth';
import './SuspensionBanner.css';

/**
 * Banner pinned to the top of every studio page when the kid's
 * account is suspended. Three states:
 *
 *  1. No appeal filed → "your account is paused" + form to file one.
 *  2. Pending appeal → "we're reviewing your appeal" + the message
 *     they filed.
 *  3. Resolved appeal → "the review is done" + decision copy.
 *     `overturned` clears `user.suspended`, so this branch is
 *     reached only briefly until the next /me refresh, but it's the
 *     right thing to render in the meantime.
 *
 * The banner does NOT block reads — kids can still see and edit
 * their own work locally while suspended. Server writes are gated
 * by `require_active_user` on the backend.
 */
export function SuspensionBanner(): JSX.Element | null {
  const { data: user } = useCurrentUser();
  const { data: appeal } = useMyAppeal();
  const fileAppeal = useFileAppeal();
  const [message, setMessage] = useState('');

  if (!user || !user.suspended) return null;

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (message.trim().length < 10) return;
    fileAppeal.mutate({ message: message.trim() }, { onSuccess: () => setMessage('') });
  };

  return (
    <div className="bl-suspended" role="alert">
      <div className="bl-suspended__inner">
        <h2 className="bl-suspended__title">Your account is paused</h2>
        <p className="bl-suspended__lead">
          A grown-up at BytaApps paused your account. You can still see and edit your projects on
          this device — but you can&rsquo;t share to the gallery, report submissions, or upload
          samples until this is sorted out.
        </p>

        {!appeal && (
          <form className="bl-suspended__form" onSubmit={onSubmit}>
            <label className="bl-suspended__label">
              <span>Tell a grown-up what happened (10 characters or more)</span>
              <textarea
                rows={3}
                maxLength={2000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Be honest — short is fine."
              />
            </label>
            <div className="bl-suspended__actions">
              <Button type="submit" disabled={fileAppeal.isPending || message.trim().length < 10}>
                {fileAppeal.isPending ? 'Sending…' : 'Send appeal'}
              </Button>
            </div>
            {fileAppeal.error && (
              <p className="bl-suspended__error" role="alert">
                {fileAppeal.error.message}
              </p>
            )}
          </form>
        )}

        {appeal && appeal.decision === null && (
          <div className="bl-suspended__pending">
            <p>
              <strong>Appeal filed.</strong> A grown-up at BytaApps is reading it. We&rsquo;ll let
              you know when they decide.
            </p>
            <blockquote className="bl-suspended__quote">{appeal.message}</blockquote>
          </div>
        )}

        {appeal && appeal.decision === 'upheld' && (
          <div className="bl-suspended__resolved">
            <p>
              <strong>Review done.</strong> A grown-up looked at your appeal but decided to keep
              your account paused for now.
            </p>
            {appeal.decision_note && (
              <blockquote className="bl-suspended__quote">{appeal.decision_note}</blockquote>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
